import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { IncidentStatus } from "@no-busy-work/ops-bridge-protocol";
import { BUTTON_TYPE, CLOSEOUT_GATE, OPS_ACTION_ENDPOINT_KIND, UI_TONE, WORKFLOW_CLEAR_STATES, WORKFLOW_STATUS } from "../OpsCenter.Config";
import { actionRows, actorRefLabel, closeoutWorkflowRows, resolutionReadiness } from "../OpsCenter.Data";
import type { IncidentDetail, OpsCenterActions, OpsCenterCopy, OpsCenterState } from "../OpsCenter.Types";
import { statusLabel } from "../i18n/OpsCenter.I18n";
import { StatusPill, TextAreaField, formatLabel, submit } from "./OpsCenterScreen.Shared";

const ROOT_CAUSE_GATE_ID = CLOSEOUT_GATE.RootCause;

type ReadinessRow = ReturnType<typeof resolutionReadiness>["rows"][number];

export function ResolutionProgress({ state, detail, actions, copy }: {
  readonly state: OpsCenterState;
  readonly detail: IncidentDetail;
  readonly actions: OpsCenterActions;
  readonly copy: OpsCenterCopy;
}): ReactNode {
  const readiness = resolutionReadiness(detail, state.rootCause, copy);
  const orderedRows = closeoutWorkflowRows(readiness);
  const firstBlocked = orderedRows.find((row) => !row.ready) ?? null;
  const [selectedId, setSelectedId] = useState(firstBlocked?.id ?? orderedRows[0]?.id ?? "");
  const selectedRow = orderedRows.find((row) => row.id === selectedId) ?? firstBlocked ?? orderedRows[0];
  const selectedIndex = orderedRows.findIndex((row) => row.id === selectedRow?.id);
  const readyCount = orderedRows.filter((row) => row.ready).length;
  const totalCount = orderedRows.length;
  const blockerCount = totalCount - readyCount;
  const isClosed = detail.incident.status === IncidentStatus.Resolved || detail.incident.status === IncidentStatus.Closed;
  const showResolveControls = !isClosed && (readiness.ready || selectedRow?.id === ROOT_CAUSE_GATE_ID);
  const showReopenControls = isClosed;
  const contextRows = closeoutContextRows(selectedRow, detail, state.rootCause, copy);

  useEffect(() => {
    const current = orderedRows.find((row) => row.id === selectedId);
    if (!current || (current.ready && firstBlocked)) {
      setSelectedId(firstBlocked?.id ?? orderedRows[0]?.id ?? "");
    }
  }, [firstBlocked?.id, orderedRows, selectedId]);

  if (!selectedRow) {
    return null;
  }

  const selectRow = (row: ReadinessRow): void => {
    setSelectedId(row.id);
  };

  return (
    <section className="closeout-control" aria-label={copy.sections.resolutionReadiness}>
      <div className="closeout-control-head">
        <div>
          <p className="eyebrow">{copy.sections.closeout}</p>
          <h2>{readiness.ready ? copy.labels.resolveReady : `${copy.labels.safeNextAction}: ${firstBlocked?.label ?? selectedRow.label}`}</h2>
        </div>
        <div className="closeout-score" aria-label={formatLabel(copy.labels.closeoutScore, { ready: String(readyCount), total: String(totalCount) })}>
          <strong>{readyCount}/{totalCount}</strong>
          <span>{blockerCount > 0 ? `${blockerCount} ${copy.labels.readinessBlockers}` : copy.labels.readinessReady}</span>
        </div>
      </div>
      <div className="closeout-path" role="list" aria-label={copy.sections.resolutionReadiness}>
        {orderedRows.map((row, index) => (
          <button
            aria-current={row.id === selectedRow.id ? "step" : undefined}
            className={segmentClass(row, selectedRow)}
            key={row.id}
            role="listitem"
            title={`${row.label}: ${row.summary}`}
            type={BUTTON_TYPE.Button}
            onClick={() => selectRow(row)}
          >
            <span className="closeout-step-index">{index + 1}</span>
            <span className="closeout-step-label">{shortGateLabel(row, copy)}</span>
          </button>
        ))}
      </div>
      <div className="closeout-active-step">
        <div className="closeout-step-main">
          <div className="closeout-step-copy">
            <span className="closeout-step-kicker">{formatLabel(copy.labels.closeoutStep, { step: String(selectedIndex + 1), total: String(totalCount) })}</span>
            <strong>{selectedRow.label}</strong>
            <p>{selectedRow.summary}</p>
          </div>
          <dl className="closeout-step-context">
            {contextRows.map(([label, value]) => (
              <div key={`${selectedRow.id}:${label}`} className="closeout-step-context-row">
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
          <GateEvidenceFields actions={actions} copy={copy} row={selectedRow} state={state} />
        </div>
        <div className="closeout-step-status">
          <StatusPill label={selectedRow.ready ? copy.status.ready : copy.status.blocked} tone={selectedRow.ready ? UI_TONE.Ok : UI_TONE.Warn} />
          <GateActionButton actions={actions} busy={state.busy} copy={copy} row={selectedRow} />
        </div>
      </div>
      {showResolveControls ? (
        <form className="closeout-resolve-form" onSubmit={(event) => submit(event, actions.resolveIncident)}>
          <TextAreaField label={copy.forms.rootCause} name="rootCause" rows={2} value={state.rootCause} onChange={actions.setRootCause} />
          <TextAreaField label={copy.labels.customerSafeSummary} name="customerSafeSummary" rows={2} value={state.customerSafeSummary} onChange={actions.setCustomerSafeSummary} />
          <div className="closeout-resolve-actions">
            <p className={readiness.ready ? "subtle-copy resolve-ready-copy" : "subtle-copy resolve-blocked-copy"}>
              {readiness.ready ? copy.labels.resolveReady : copy.labels.resolveLocked}
            </p>
            <button className={readiness.ready ? "primary-button" : "secondary-button"} disabled={state.busy || !readiness.ready} type={BUTTON_TYPE.Submit}>
              {state.busy ? copy.auth.working : copy.forms.resolveIncident}
            </button>
          </div>
        </form>
      ) : null}
      {showReopenControls ? (
        <form className="closeout-resolve-form closeout-reopen-form" onSubmit={(event) => submit(event, actions.reopenIncident)}>
          <TextAreaField label={copy.forms.reopenReason} name="reopenReason" rows={2} value={state.reopenReason} onChange={actions.setReopenReason} />
          <div className="closeout-resolve-actions">
            <p className="subtle-copy resolve-ready-copy">{copy.labels.reopenReady}</p>
            <button className="secondary-button" disabled={state.busy || !state.reopenReason.trim()} type={BUTTON_TYPE.Submit}>
              {state.busy ? copy.auth.working : copy.forms.reopenIncident}
            </button>
          </div>
        </form>
      ) : null}
      {!isClosed && !readiness.ready ? (
        <div className="closeout-denied-check">
          <p className="closeout-lock-copy">{copy.labels.resolveLocked}</p>
          <div className="closeout-denied-actions">
            <button className="secondary-button" disabled={true} type={BUTTON_TYPE.Button}>
              {copy.forms.resolveIncident}
            </button>
            <button className="secondary-button" disabled={state.busy} type={BUTTON_TYPE.Button} onClick={() => void actions.checkResolveEligibility()}>
              {state.busy ? copy.auth.working : copy.actions.checkResolveEligibility}
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}

function GateEvidenceFields({ actions, copy, row, state }: {
  readonly actions: OpsCenterActions;
  readonly copy: OpsCenterCopy;
  readonly row: ReadinessRow;
  readonly state: OpsCenterState;
}): ReactNode {
  if (row.ready || row.id === ROOT_CAUSE_GATE_ID) {
    return null;
  }
  if (row.id === CLOSEOUT_GATE.HealthVerification || row.id === CLOSEOUT_GATE.VerificationEvidence) {
    return <TextAreaField label={copy.labels.verificationEvidence} name="verificationEvidence" rows={2} value={state.verificationEvidence} onChange={actions.setVerificationEvidence} />;
  }
  if (row.id === CLOSEOUT_GATE.RemediationProof || row.id === CLOSEOUT_GATE.CodeBridge) {
    return <TextAreaField label={copy.labels.remediationProof} name="remediationProof" rows={2} value={state.remediationProof} onChange={actions.setRemediationProof} />;
  }
  if (row.id === CLOSEOUT_GATE.OutageNotice || row.id === CLOSEOUT_GATE.CustomerNotifications || row.id === CLOSEOUT_GATE.ResolutionNotifications) {
    return <TextAreaField label={copy.labels.customerSafeSummary} name="customerSafeSummary" rows={2} value={state.customerSafeSummary} onChange={actions.setCustomerSafeSummary} />;
  }
  return null;
}

function GateActionButton({ actions, busy, copy, row }: {
  readonly actions: OpsCenterActions;
  readonly busy: boolean;
  readonly copy: OpsCenterCopy;
  readonly row: ReadinessRow;
}): ReactNode {
  if (row.ready) {
    return <span className={`closeout-state-label ${UI_TONE.Ok}`}>{copy.status.ready}</span>;
  }
  if (row.id === ROOT_CAUSE_GATE_ID && row.actionTab) {
    return (
      <button className="secondary-button closeout-action-button" disabled={busy} type={BUTTON_TYPE.Button} onClick={() => actions.setActiveTab(row.actionTab!)}>
        {row.actionLabel ?? copy.forms.rootCause}
      </button>
    );
  }
  return (
    <button className="primary-button closeout-action-button" disabled={busy} type={BUTTON_TYPE.Button} onClick={() => void actions.recordReadinessGate(row.id)}>
      {busy ? copy.auth.working : gateActionLabel(row, copy)}
    </button>
  );
}
function segmentClass(row: ReadinessRow, selectedRow: ReadinessRow): string {
  const stateClass = row.ready ? UI_TONE.Ok : WORKFLOW_STATUS.Blocked;
  return row.id === selectedRow.id ? `closeout-path-segment ${stateClass} active` : `closeout-path-segment ${stateClass}`;
}

function shortGateLabel(row: ReadinessRow, copy: OpsCenterCopy): string {
  const labels: Readonly<Record<string, string>> = {
    [CLOSEOUT_GATE.HealthVerification]: copy.labels.gateHealthShort,
    [CLOSEOUT_GATE.RemediationProof]: copy.labels.gateProofShort,
    [CLOSEOUT_GATE.VerificationEvidence]: copy.labels.gateVerifyShort,
    [CLOSEOUT_GATE.ApprovalQueue]: copy.labels.gateApprovalsShort,
    [CLOSEOUT_GATE.OutageNotice]: copy.labels.gateNoticeShort,
    [CLOSEOUT_GATE.CustomerNotifications]: copy.labels.gateCustomersShort,
    [CLOSEOUT_GATE.OwnerAcknowledgement]: copy.labels.gateOwnerShort,
    [CLOSEOUT_GATE.RootCause]: copy.labels.gateCauseShort,
    [CLOSEOUT_GATE.ResolutionNotifications]: copy.labels.gateResolutionShort,
    [CLOSEOUT_GATE.CodeBridge]: copy.labels.gateCodeShort
  };
  return labels[row.id] ?? row.label;
}

function gateActionLabel(row: ReadinessRow, copy: OpsCenterCopy): string {
  const labels: Readonly<Record<string, string>> = {
    [CLOSEOUT_GATE.HealthVerification]: copy.actions.recordHealthCheck,
    [CLOSEOUT_GATE.RemediationProof]: copy.actions.recordRemediationProof,
    [CLOSEOUT_GATE.VerificationEvidence]: copy.actions.recordVerification,
    [CLOSEOUT_GATE.ApprovalQueue]: copy.actions.clearApprovals,
    [CLOSEOUT_GATE.OutageNotice]: copy.actions.postOutageNotice,
    [CLOSEOUT_GATE.CustomerNotifications]: copy.actions.sendCustomerNotice,
    [CLOSEOUT_GATE.OwnerAcknowledgement]: copy.actions.acknowledgeOwner,
    [CLOSEOUT_GATE.RootCause]: copy.forms.rootCause,
    [CLOSEOUT_GATE.ResolutionNotifications]: copy.actions.sendResolutionNotice,
    [CLOSEOUT_GATE.CodeBridge]: copy.actions.recordCodeBridgeProof
  };
  return labels[row.id] ?? copy.actions.recordGate;
}

function closeoutContextRows(row: ReadinessRow, detail: IncidentDetail, rootCause: string, copy: OpsCenterCopy): readonly (readonly [string, string])[] {
  const summary = detail.communicationSummary;
  const incident = detail.incident;
  const openApprovals = actionRows(detail, copy).filter((item) => item.requiresApproval && !workflowActionCleared(item)).length;
  const openDecisions = detail.decisions.filter((item) => !workflowStateCleared(item.state) && !workflowStateCleared(item.approvalState)).length;
  const openEscalations = detail.ownerEscalations.filter((item) => !workflowStateCleared(item.state)).length;
  const openCodeBridge = actionRows(detail, copy).filter((item) => item.endpointKind === OPS_ACTION_ENDPOINT_KIND.CodeBridgeSession && !workflowActionCleared(item)).length;
  switch (row.id) {
    case CLOSEOUT_GATE.OutageNotice:
      return [
        [copy.labels.actionState, statusLabel(copy, summary.outageNoticeState)] as const,
        [copy.labels.complaints, String(summary.complaintCount)] as const,
        [copy.labels.impacted, String(summary.impactedCustomerCount)] as const
      ];
    case CLOSEOUT_GATE.CustomerNotifications:
      return [
        [copy.labels.actionState, statusLabel(copy, summary.customerNotificationState)] as const,
        [copy.labels.complaints, String(summary.complaintCount)] as const,
        [copy.labels.impacted, String(summary.impactedCustomerCount)] as const
      ];
    case CLOSEOUT_GATE.ResolutionNotifications:
      return [
        [copy.labels.actionState, statusLabel(copy, summary.resolutionNotificationState)] as const,
        [copy.labels.deliveryRisks, String(summary.deliveryRiskCount)] as const,
        [copy.labels.impacted, String(summary.impactedCustomerCount)] as const
      ];
    case CLOSEOUT_GATE.ApprovalQueue:
      return [
        [copy.sections.approvalQueue, String(openApprovals + openDecisions)] as const,
        [copy.sections.ownerEscalation, String(openEscalations)] as const,
        [copy.labels.nextStep, row.actionLabel ?? copy.labels.openActions] as const
      ];
    case CLOSEOUT_GATE.OwnerAcknowledgement:
      return [
        [copy.labels.owner, actorRefLabel(incident.accountability.serviceOwnerRef, copy)] as const,
        [copy.labels.actionState, summary.ownerAcknowledged ? copy.status.ready : copy.status.blocked] as const,
        [copy.labels.nextStep, row.actionLabel ?? copy.labels.openActions] as const
      ];
    case CLOSEOUT_GATE.CodeBridge:
      return [
        [copy.sections.codeBridge, String(openCodeBridge)] as const,
        [copy.labels.actionState, openCodeBridge ? copy.status.blocked : copy.status.ready] as const,
        [copy.labels.nextStep, row.actionLabel ?? copy.labels.openActions] as const
      ];
    case CLOSEOUT_GATE.RootCause:
      return [
        [copy.forms.rootCause, rootCause.trim() ? copy.status.ready : copy.status.pending] as const,
        [copy.labels.actionState, statusLabel(copy, incident.status)] as const,
        [copy.labels.nextStep, row.actionLabel ?? copy.forms.rootCause] as const
      ];
    case CLOSEOUT_GATE.HealthVerification:
    case CLOSEOUT_GATE.RemediationProof:
    case CLOSEOUT_GATE.VerificationEvidence:
      return [
        [copy.sections.evidence, row.summary] as const,
        [copy.sections.timeline, String(detail.timeline.length)] as const,
        [copy.labels.nextStep, row.actionLabel ?? copy.labels.openEvidence] as const
      ];
    default:
      return [
        [copy.labels.status, statusLabel(copy, incident.status)] as const,
        [copy.labels.actionState, row.summary] as const,
        [copy.labels.nextStep, row.actionLabel ?? copy.status.review] as const
      ];
  }
}

function workflowActionCleared(row: ReturnType<typeof actionRows>[number]): boolean {
  return workflowStateCleared(row.status) || workflowStateCleared(row.approvalState);
}

function workflowStateCleared(value: string | null | undefined): boolean {
  return typeof value === "string" && WORKFLOW_CLEAR_STATES.has(value);
}
